import React, { createContext, useContext, useState } from "react";
import axiosInstance, { handleApiError } from "../services/axiosService";
import { createChecksum } from "../commonFunctions/fileFolderConfig";
import { useAppOrgContext } from "./AppOrgContext";

const VariableContextApi = createContext();
const useVariableContext = () => useContext(VariableContextApi);

const VariableContext = ({ children }) => {
  const [selectedVariable, setSelectedVariable] = useState({ key: "", value: "", type: "" });
  const [variableData, setVariableData] = useState([]);
  const { currentChecksum } = useAppOrgContext();

  const defaultVariables = [
    { key: "URL_SHORTNER_API", value: "", type: "env" },
    { key: "SMS_PROVIDER", value: "", type: "env" },
    { key: "EMAIL_PROVIDER", value: "", type: "env" },
    { key: "ENABLE_EMAIL_INCOMING", value: "", type: "env" },
    { key: "EMAIL_AUTH_TYPE", value: "", type: "env" },
    { key: "EMAIL_HELO", value: "", type: "env" },
    { key: "MSG91_API_KEY", value: "", type: "env" },
    { key: "MSG91_SENDER_ID", value: "", type: "env" },
    { key: "MSG91_API_URL", value: "", type: "env" },
    { key: "MSG91_API_TOKEN", value: "", type: "env" },
    { key: "MSG91_SUBDOMAIN", value: "", type: "env" },
    { key: "MSG91_ACC_SID", value: "", type: "env" },
    { key: "RDBMS_MYSQL_HOST", value: "", type: "env" },
    { key: "RDBMS_MYSQL_PORT", value: "", type: "env" },
    { key: "RDBMS_MYSQL_USER", value: "", type: "env" },
    { key: "RDBMS_MYSQL_PASS", value: "", type: "env" },
    { key: "RDBMS_MYSQL_DB", value: "", type: "env" },
    { key: "PG_HOST", value: "", type: "env" },
    { key: "PG_PORT", value: "", type: "env" },
    { key: "PG_USER", value: "", type: "env" },
    { key: "PG_PASS", value: "", type: "env" },
    { key: "PG_NAME", value: "", type: "env" },
    { key: "GOOGLE_DOC_TOKEN", value: "", type: "env" },
    { key: "DISABLE_ALL_SCREEN_ACCESS", value: "false", type: "env" }
    // { key: "COMMUNITY_USERS_LOGIN", value: "false", type: "env" },
    // { key: "COMMUNITY_USERS_SIGNUP", value: "false", type: "env" }
  ];

  //check if key already exists and has value
  const uniqueVariableData = (data) => {
    const dataKeys = new Set(data.map((item) => item.key));

    const mergedData = defaultVariables.reduce(
      (acc, variable) => {
        if (!dataKeys.has(variable.key)) {
          acc.push({ key: variable.key, value: "", type: "env" });
        }
        return acc;
      },
      [...data]
    );

    return mergedData;
  };

  const checkDefaultKey = (data) => {
    const defaultKeys = defaultVariables.map((item) => item.key);
    return data.map((item) => ({
      ...item,
      status: defaultKeys.includes(item.key) ? "default" : "custom"
    }));
  };

  const fetchAllVariables = async () => {
    try {
      const { data } = await axiosInstance.get(`/appConfig`);
      const newData = uniqueVariableData(data);
      const modifiedData = checkDefaultKey(newData);
      setVariableData(modifiedData);

      const newChecksum = createChecksum(newData);
      currentChecksum.current = newChecksum;
    } catch (error) {
      handleApiError(error);
    }
  };

  const getVariableConfig = () => {
    const config = variableData.filter((variable) => variable.value !== "");
    return config.map(({ ...rest }) => rest);
  };

  const handleSaveVariables = async () => {
    try {
      const config = getVariableConfig();

      const { message } = await axiosInstance.put(`/appConfig-bulk/`, config);

      const newChecksum = createChecksum(config);
      currentChecksum.current = newChecksum;
      return { data: config, message };
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <VariableContextApi.Provider
      value={{
        selectedVariable,
        variableData,
        fetchAllVariables,
        setSelectedVariable,
        setVariableData,
        getVariableConfig,
        handleSaveVariables
      }}
    >
      {children}
    </VariableContextApi.Provider>
  );
};

export default VariableContext;
export { useVariableContext };
